import React, { useEffect, useState } from "react";
import { popsApi, fmtUSD } from "../../lib/api";
import { Network, ArrowRight, EyeOff } from "lucide-react";

export default function HierarchyTab({ period }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    popsApi.hierarchy(period).then(setData).catch(console.error);
  }, [period]);

  if (!data) return <div className="text-muted-foreground text-sm">Loading hierarchy…</div>;
  const ms = data.margin_split;

  const boxes = [
    { label: "Google Cloud (provider)", sub: "bills the L1 payer", value: ms.provider_billed_usd, cls: "bg-white border-border" },
    { label: data.tenants.find((t) => t.layer === "L1")?.name, sub: `L1 distributor · margin ${fmtUSD(ms.l1_margin_usd)}`, value: ms.l1_revenue_usd, cls: "bg-[#0B2545] text-white border-[#0B2545]" },
    { label: data.tenants.find((t) => t.layer === "L2")?.name, sub: `L2 MSP · margin ${fmtUSD(ms.l2_margin_usd)}`, value: ms.l2_revenue_usd, cls: "bg-[#00579e] text-white border-[#00579e]" },
    { label: "End customers (6)", sub: "invoiced in USD / EUR / GBP / INR", value: ms.l2_revenue_usd, cls: "bg-white border-border" },
  ];

  return (
    <div className="space-y-4" data-testid="pops-hierarchy-tab">
      <div className="cs-card p-6">
        <div className="flex items-center gap-2 mb-5">
          <Network className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">Three-layer commercial chain · {period}</h3>
        </div>
        <div className="flex flex-col lg:flex-row items-stretch gap-2">
          {boxes.map((b, i) => (
            <React.Fragment key={i}>
              <div className={`flex-1 border rounded-sm p-4 ${b.cls}`} data-testid={`hierarchy-box-${i}`}>
                <div className="text-xs font-semibold">{b.label}</div>
                <div className={`text-[10px] mt-0.5 ${b.cls.includes("text-white") ? "text-white/70" : "text-muted-foreground"}`}>{b.sub}</div>
                <div className="cs-mono text-lg font-semibold mt-2">{fmtUSD(b.value)}</div>
              </div>
              {i < boxes.length - 1 && (
                <div className="flex items-center justify-center shrink-0">
                  <ArrowRight className="w-4 h-4 text-muted-foreground rotate-90 lg:rotate-0" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex items-start gap-2 mt-4 p-3 bg-[hsl(var(--cs-tint))] border border-border rounded-sm">
          <EyeOff className="w-4 h-4 text-[#00579e] mt-0.5 shrink-0" />
          <p className="text-xs text-foreground">{data.note}</p>
        </div>
      </div>

      <div className="cs-card p-5" data-testid="reseller-links-panel">
        <h3 className="cs-heading text-sm font-semibold mb-3">Reseller Links (one-way scoped publication)</h3>
        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              {["Link", "From (L1)", "To (L2)", "Scope", "Status", "Since"].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.reseller_links.map((rl) => (
              <tr key={rl.id} className="border-b border-border last:border-0" data-testid={`reseller-link-${rl.id}`}>
                <td className="px-3 py-2 cs-mono">{rl.id}</td>
                <td className="px-3 py-2 font-medium">{data.tenants.find((t) => t.id === rl.from_tenant)?.name}</td>
                <td className="px-3 py-2 font-medium">{data.tenants.find((t) => t.id === rl.to_tenant)?.name}</td>
                <td className="px-3 py-2 cs-mono text-[11px] text-muted-foreground">
                  {rl.scope.providers.join(",")} · {rl.scope.cost_types.join(",")} · accounts: {rl.scope.accounts}
                </td>
                <td className="px-3 py-2">
                  <span className="px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold bg-[#dcfce7] text-[#15803d]">{rl.status}</span>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{(rl.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[11px] text-muted-foreground mt-3">{data.reseller_links[0]?.note}</p>
      </div>
    </div>
  );
}
